<template>
  <div v-if="!loading">
    <setting-item
      name="Nota Fiscal Eletrônica"
      description="Emissão automatizada e simplificada de NF-e."
      info="Trata-se de um serviço adicional, necessário contratar pacote de serviço"
      type="switch"
      :value="form.fiscalDocument.enabled"
      :action-icon="['fal', 'cog']"
      :action-fn="() => { fiscalDocumentModal.show = true }"
      :action-disabled="form.fiscalDocument.enabled === false"
      @change="updateValue('enabled', $event, 'fiscalDocument')"
    ></setting-item>

    <setting-item
      name="Assinatura digital"
      description="Serviço de assinatura eletrônica de documentos"
      info="Para assinar eletronicamente, é necessário ter ou adquirir um certificado digital"
      type="switch"
      :value="form.digitalSignature.enabled"
      :action-disabled="false"
      @change="updateValue('enabled', $event, 'digitalSignature')"
    ></setting-item>

    <setting-item
      name="Doctoralia"
      description="Integração com a plataforma Doctoralia"
      info="Para utilizar essa integração é necessário ser cliente Doctoralia"
      type="switch"
      :value="form.doctoralia.enabled"
      :action-icon="['fal', 'cog']"
      :action-fn="() => { doctoraliaModal.show = true }"
      :action-disabled="form.doctoralia.enabled === false"
      @change="updateValue('enabled', $event, 'doctoralia')"
    ></setting-item>

    <setting-item
      name="Noa Notes"
      description="Integração com a plataforma Noa Notes"
      type="switch"
      :value="form.noaNotes.enabled"
      :action-icon="['fal', 'cog']"
      :action-fn="() => { noaNotesModal.show = true }"
      :action-disabled="form.noaNotes.enabled === false"
      @change="updateValue('enabled', $event, 'noaNotes')"
    ></setting-item>

    <setting-item
      name="Logic"
      description="Integração de laudos, visualizador DICOM e protocolos de exames"
      info="Para utilizar essa integração é necessário ser cliente da Logic"
      type="switch"
      :value="false"
      :action-icon="['fal', 'cog']"
      :soon="true"
    ></setting-item>

    <dx-modal v-model="doctoraliaModal.show" title="Doctoralia">
      <div class="form-group">
        <label for="client-id" class="form-label">Client ID</label>
        <input
          class="form-input"
          id="client-id"
          type="text"
          v-model="form.doctoralia.clientId"
          placeholder="Client ID"
        >
      </div>
      <div class="form-group">
        <label for="client-secret" class="form-label">Client Secret</label>
        <input
          class="form-input"
          id="client-secret"
          type="text"
          v-model="form.doctoralia.clientSecret"
          placeholder="Client Secret"
        >
      </div>
      <div class="form-group">
        <label for="client-secret" class="form-label tooltip">Dias de agenda liberados</label>
        <div class="input-group">
          <input
            class="form-input"
            id="client-secret"
            type="number"
            v-model="form.doctoralia.availableDays"
            min="7"
            max="120"
            placeholder="14 - 90"
          >
          <span class="input-group-addon">dias</span>
        </div>
        <div class="text-info mt-1 text-small">
          Define quantos dias à frente os horários ficarão disponíveis na Doctoralia.
        </div>
      </div>
      <div class="form-group">
        <label for="client-webhook" class="form-label">Endereço Webhook</label>
        <div class="input-group">
          <input
            class="form-input"
            id="client-webhook"
            type="text"
            :value="webhookUrl"
            readonly="readonly"
          >
          <button
            class="btn input-group-btn btn-info btn-icon tooltip"
            @click="copyWebhookToClipboard"
            :disabled="!isSet"
            data-tooltip="Copiar"
          ><fa-icon :icon="['fal', 'copy']"></fa-icon></button>
        </div>
        <div class="text-info mt-1 text-small" v-if="isSet">
          Essa URL deve ser enviada à equipe da Doctoralia para configurar a integração.
        </div>
      </div>
      <template v-slot:footer>
        <button
          class="btn btn-primary"
          :class="{ loading: saving }"
          :disabled="saving"
          @click="updateGroup('doctoralia')"
        >Salvar</button>
        <button class="btn ml-2" @click="doctoraliaModal.show = false">Sair</button>
      </template>
    </dx-modal>

    <dx-modal v-model="noaNotesModal.show" title="Noa Notes">
      <div class="form-group">
        <label for="noa-notes-client-id" class="form-label">Client ID</label>
        <input
          class="form-input"
          id="noa-notes-client-id"
          type="text"
          v-model="form.noaNotes.clientId"
          placeholder="Client ID"
        >
      </div>
      <div class="form-group">
        <label for="noa-notes-client-secret" class="form-label">Client Secret</label>
        <input
          class="form-input"
          id="noa-notes-client-secret"
          type="text"
          v-model="form.noaNotes.clientSecret"
          placeholder="Client Secret"
        >
      </div>
      <template v-slot:footer>
        <button
          class="btn btn-primary"
          :class="{ loading: saving }"
          :disabled="saving || !isNoaNotesValid"
          @click="updateGroup('noaNotes')"
        >Salvar</button>
        <button class="btn ml-2" @click="noaNotesModal.show = false">Sair</button>
      </template>
    </dx-modal>

    <fiscal-document-modal
      v-if="fiscalDocumentModal.show"
      @close="fiscalDocumentModal.show = false"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { mergeFrom } from '@/helpers/object';
import SettingItem from '../SettingItem.vue';
import FiscalDocumentModal from './FiscalDocumentModal.vue';

export default {
  props: {
    loading: {
      type: Boolean,
    },
    settings: {
      type: Object,
    },
  },
  components: {
    SettingItem,
    FiscalDocumentModal,
  },
  data() {
    return {
      saving: false,
      form: this.blankForm(),
      doctoraliaModal: {
        show: false,
      },
      noaNotesModal: {
        show: false,
      },
      fiscalDocumentModal: {
        show: false,
      },
    };
  },
  watch: {
    loading() {
      this.updateForm();
    },
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    customerId() {
      return this.user && this.user.facility ? this.user.facility.customerId : null;
    },
    isSet() {
      return this.form.doctoralia.clientId && this.form.doctoralia.clientSecret;
    },
    webhookUrl() {
      return this.isSet ? `https://api.stenci.pro/hook/doctoralia/${this.customerId}` : '--';
    },
    isNoaNotesValid() {
      return this.form.noaNotes.clientId && this.form.noaNotes.clientSecret;
    },
  },
  methods: {
    updateForm() {
      this.form = mergeFrom(this.form, this.settings);
    },
    async updateGroup(group) {
      if (group === 'noaNotes' && !this.isNoaNotesValid) {
        this.$toast.error('Client ID e Client Secret são obrigatórios');
        return;
      }
      await this.update({
        [group]: this.form[group],
      });
    },
    async updateValue(key, value, group) {
      await this.update({
        [group]: {
          [key]: value,
        },
      });
      this.form[group][key] = value;
    },
    async update(data = null) {
      this.saving = true;
      try {
        await this.$http.put('/facility-settings', data || this.form);
        this.$toast.success('Configuração salva com sucesso');
      } catch (e) {
        this.$toast.error(e);
      }
      this.saving = false;
    },
    copyWebhookToClipboard() {
      navigator.clipboard.writeText(this.webhookUrl);
      this.$toast.success('Endereço copiado');
    },
    blankForm() {
      return {
        fiscalDocument: {
          enabled: false,
        },
        doctoralia: {
          enabled: true,
          clientId: '',
          clientSecret: '',
          availableDays: 28,
        },
        noaNotes: {
          enabled: false,
          clientId: '',
          clientSecret: '',
        },
        digitalSignature: {
          enabled: false,
        },
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./src/assets/scss/_variables.scss";
</style>
